import { useState, useEffect, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { mlflowApiRef } from '../../../api/MLFlowApi';
import { NO_DEVICES_VALUE } from '../../../utils/transforms';
import { validateDeploymentInputs } from '../../../utils/validation';
import { DeploymentResult } from '../types/types';

export const useModelDeployment = (entity: any) => {
  const mlflowApi = useApi(mlflowApiRef);
  
  const [selectedDevice, setSelectedDevice] = useState('');
  const [deviceInput, setDeviceInput] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<DeploymentResult | null>(null);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versionInput, setVersionInput] = useState('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isDeployDisabled = deploying || !selectedDevice || selectedDevice === NO_DEVICES_VALUE;

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const pollDeploymentStatus = async (deploymentId: string) => {
    try {
      const statusResult = await mlflowApi.getDeploymentStatus(deploymentId);
      
      setDeployResult(prev => {
        const existingLogs = prev?.logs || [];
        const newLogs = statusResult.logs || [];
        const allLogs = [...existingLogs];
        
        const statusCheckLog = {
          timestamp: new Date().toISOString(),
          message: `Status check: ${statusResult.status} - ${statusResult.message || statusResult.status_message || ''}`,
        };
        
        const statusCheckExists = existingLogs.some(
          log => log.timestamp === statusCheckLog.timestamp && log.message === statusCheckLog.message
        );
        
        if (!statusCheckExists) {
          allLogs.push(statusCheckLog);
        }
        
        for (const newLog of newLogs) {
          const isDuplicate = existingLogs.some(
            log => log.timestamp === newLog.timestamp && log.message === newLog.message
          );
          if (!isDuplicate) {
            allLogs.push(newLog);
          }
        }
        
        return {
          success: prev?.success ?? true,
          deployment_id: prev?.deployment_id ?? deploymentId,
          status: statusResult.status,
          message: statusResult.message,
          error: statusResult.error,
          logs: allLogs,
        };
      });

      if (statusResult.status === 'COMPLETED' || statusResult.status === 'FAILED') {
        stopPolling();
        setDeploying(false);
      }
    } catch (error) {
      stopPolling();
      setDeployResult(prev => ({
        success: false,
        deployment_id: prev?.deployment_id ?? deploymentId,
        status: 'FAILED',
        message: prev?.message,
        error: error instanceof Error ? error.message : 'Failed to check deployment status',
      }));
      setDeploying(false);
    }
  };

  const handleDeployModel = async () => {
    const modelComponentName = entity?.spec?.modelInfo?.modelComponentName;
    
    const validation = validateDeploymentInputs(selectedDevice, modelComponentName);
    if (!validation.valid) {
      setDeployResult({ success: false, message: validation.errorMessage });
      return;
    }

    setDeploying(true);
    setDeployResult(null);
    stopPolling();

    try {
      const initialLog = {
        timestamp: new Date().toISOString(),
        message: `Beginning deployment for component: ${modelComponentName} to device: ${selectedDevice}`,
      };

      const result = await mlflowApi.triggerWorkflow({
        components: [modelComponentName],
        device_name: selectedDevice,
        model_version: selectedVersion,
      });

      const responseLog = {
        timestamp: new Date().toISOString(),
        message: `Deployment ${result.status || 'initiated'}: ${result.message || 'Request sent successfully'}`,
      };

      const allLogs = [
        initialLog,
        responseLog,
        ...(result.logs || []),
      ];

      setDeployResult({
        ...result,
        logs: allLogs,
      });

      if (result.deployment_id && result.status !== 'FAILED') {
        setTimeout(() => {
          pollDeploymentStatus(result.deployment_id!);
          
          pollingIntervalRef.current = setInterval(() => {
            pollDeploymentStatus(result.deployment_id!);
          }, 15000);
        }, 15000);
      } else {
        setDeploying(false);
      }
      
    } catch (error) {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: `Error: ${error instanceof Error ? error.message : 'Failed to trigger deployment'}`,
      };

      setDeployResult({
        success: false,
        status: 'FAILED',
        message: error instanceof Error ? error.message : 'Failed to trigger deployment',
        logs: [errorLog],
      });
      setDeploying(false);
    }
  };

  return {
    selectedDevice,
    setSelectedDevice,
    deviceInput,
    setDeviceInput,
    selectedVersion,
    setSelectedVersion,
    versionInput,
    setVersionInput,
    deploying,
    deployResult,
    isDeployDisabled,
    handleDeployModel,
  };
};
